const GROUP_ORDER = ["recent", "actions", "pages", "records", "help", "utilities"];

export const GROUP_LABELS = {
    recent: "Recent",
    actions: "Actions",
    pages: "Pages",
    records: "Records",
    help: "Help",
    utilities: "Utilities"
};

const GROUP_LIMITS = {
    recent: 6,
    actions: 8,
    pages: 10,
    records: 12,
    help: 5,
    utilities: 2
};

export function normalizeSearchText(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

function editDistance(a, b) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;

    const previous = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i += 1) {
        const current = [i];
        for (let j = 1; j <= b.length; j += 1) {
            current[j] = Math.min(
                current[j - 1] + 1,
                previous[j] + 1,
                previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
            );
        }
        previous.splice(0, previous.length, ...current);
    }
    return previous[b.length];
}

function fuzzyWordMatch(words, query) {
    if (query.length < 4 || query.includes(" ")) return null;
    const maximumDistance = query.length >= 8 ? 2 : 1;
    let best = null;
    for (const word of words) {
        if (Math.abs(word.length - query.length) > maximumDistance) continue;
        const distance = editDistance(word, query);
        if (distance <= maximumDistance && (!best || distance < best.distance)) {
            best = { word, distance };
        }
    }
    return best;
}

function bodySnippet(value, query) {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    if (!text) return "";
    const normalized = text.toLowerCase();
    const index = normalized.indexOf(query);
    if (index < 0) return text.length > 90 ? `${text.slice(0, 87)}…` : text;
    const start = Math.max(0, index - 32);
    const end = Math.min(text.length, index + query.length + 50);
    return `${start > 0 ? "…" : ""}${text.slice(start, end).trim()}${end < text.length ? "…" : ""}`;
}

export function scoreCommandItem(item, rawQuery) {
    const query = normalizeSearchText(rawQuery);
    if (!query) return null;

    const label = normalizeSearchText(item.label);
    const section = normalizeSearchText(item.sectionTitle);
    const description = normalizeSearchText(item.description);
    const searchText = normalizeSearchText(item.searchText);
    const keywords = (item.keywords || []).map(normalizeSearchText).filter(Boolean);
    const labelWords = label.split(" ").filter(Boolean);

    if (label === query) return { score: 120, matchDetail: item.kind || "Exact match" };
    if (label.startsWith(query)) return { score: 105, matchDetail: item.kind || item.sectionTitle };
    if (labelWords.some((word) => word.startsWith(query))) {
        return { score: 92, matchDetail: item.kind || item.sectionTitle };
    }
    if (label.includes(query)) return { score: 84, matchDetail: item.kind || item.sectionTitle };

    const keyword = keywords.find((value) => value === query || value.startsWith(query));
    if (keyword) return { score: 76, matchDetail: `Matches “${keyword}”` };
    if (keywords.some((value) => value.includes(query))) {
        return { score: 68, matchDetail: item.kind || item.sectionTitle };
    }
    if (section.includes(query)) return { score: 58, matchDetail: item.kind || item.sectionTitle };
    if (description.includes(query)) {
        return { score: 50, matchDetail: bodySnippet(item.description, query) };
    }

    // Server results can match indexed identifiers (for example a barcode or
    // email domain) that are deliberately omitted from the compact payload.
    if (item.serverMatch) return { score: 46, matchDetail: item.kind || item.sectionTitle };

    const fuzzy = fuzzyWordMatch([...labelWords, ...keywords.flatMap((value) => value.split(" "))], query);
    if (fuzzy) return { score: 42 - fuzzy.distance, matchDetail: `Close match for “${fuzzy.word}”` };

    if (searchText.includes(query)) {
        return { score: 28, matchDetail: bodySnippet(item.searchText, query) };
    }
    return null;
}

export function groupedCommandResults(items, query) {
    const buckets = new Map(GROUP_ORDER.map((group) => [group, []]));
    for (const item of items) {
        const match = scoreCommandItem(item, query);
        if (!match) continue;
        const group = buckets.has(item.group) ? item.group : "pages";
        buckets.get(group).push({ ...item, ...match });
    }

    return GROUP_ORDER.map((group) => {
        const results = buckets
            .get(group)
            .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
            .slice(0, GROUP_LIMITS[group]);
        return results.length ? { id: group, label: GROUP_LABELS[group], items: results } : null;
    }).filter(Boolean);
}

export function flattenCommandGroups(groups) {
    return groups.flatMap((group) => group.items);
}
